import { AfterViewInit, ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { SelectionModel } from '@angular/cdk/collections';
import { Observable, timer } from 'rxjs';
import { take } from 'rxjs/operators';
import { LoadingFacade } from '../../../../../store/state/loading';
import {
  PartnersSelectionItemWithType,
  PartnerBrief,
  PartnerListFacade,
  PartnerListSearchCriteria,
  PartnerType,
  PartnerExtIdsModel,
  PartnerServiceType,
} from '../../../../../store/state/administration';
import { ListQueryParam } from '../../../../../common/constants';
import { ListServerPagination } from '../../../../../common/components';
import { ListRange, ListServerPaginationStateModel } from '../../../../../common/models';
import { ButtonAction, ButtonType } from '../../../../action-button';
import { FilterSearch, FilterSelect, FilterSettings, SelectOption } from '../../../../list-filter';
import { TranslateService } from '@ngx-translate/core';
import { PartnersSelectionItem } from '../../../../../container-components/partners-selection';
import { isArray } from 'lodash';
import { unchangedForm } from '../../../../../common/utils';

@Component({
  selector: 'partners-selection-filter-dialog.component',
  templateUrl: './partners-selection-filter-dialog.component.html',
  styleUrls: ['./partners-selection-filter-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PartnersSelectionFilterDialogComponent
  extends ListServerPagination<PartnerBrief>
  implements OnInit, AfterViewInit, OnDestroy
{
  displayedColumns = ['select', 'name', 'type1', 'id1', 'type2', 'id2'];
  listDto$: Observable<ListServerPaginationStateModel<PartnerBrief>>;
  selection: SelectionModel<PartnerBrief> = new SelectionModel<PartnerBrief>(true, []);
  filterConfig: FilterSettings;
  searchCriteriaValue: PartnerListSearchCriteria = new PartnerListSearchCriteria({ [ListQueryParam.Active]: true });
  buttonAction = ButtonAction;
  buttonType = ButtonType;
  partnerBriefList: PartnersSelectionItem[] = [];
  predefinedType: PartnerType;
  type1Index: number;
  type2Index: number;
  initialPartnerList: number[] = [];

  isFormUnchanged: (intialPartnerValue: number[], updatedPartnerValue: number[]) => boolean = unchangedForm;

  @ViewChild(MatPaginator) paginator: MatPaginator;

  constructor(
    translateService: TranslateService,
    public dialogRef: MatDialogRef<PartnersSelectionFilterDialogComponent>,
    public partnerListFacade: PartnerListFacade,
    public loadingFacade: LoadingFacade,
    @Inject(MAT_DIALOG_DATA) public data: PartnersSelectionItem[],
  ) {
    super(translateService);
    this.listDto$ = partnerListFacade.listDto$;
    this.initialFetch = !this.predefinedType;
  }

  ngOnInit(): void {
    if (this.data) {
      this.data.forEach((row: PartnersSelectionItem) => {
        const partnerBrief = new PartnersSelectionItemWithType();
        partnerBrief.id = row.id;
        partnerBrief.name = row.name;
        this.partnerBriefList.push(partnerBrief);
        this.initialPartnerList.push(partnerBrief.id);
      });
    }
    this.initFiltersForm();
  }

  ngAfterViewInit(): void {
    super.ngAfterViewInit();
  }

  ngOnDestroy(): void {
    this.partnerListFacade.setInitialState();
  }

  fetchList(listRange: ListRange): void {
    timer(0)
      .pipe(take(1))
      .subscribe(() => {
        this.searchCriteriaValue = {
          ...this.searchCriteriaValue,
          ...listRange,
        };
        this.partnerListFacade.loadListForDialog(new PartnerListSearchCriteria(this.searchCriteriaValue));
      });
  }

  fetchListBySearchCriteria(filterValue: PartnerListSearchCriteria): void {
    if (!filterValue.partnerType || isArray(filterValue.partnerType)) {
      this.searchCriteriaValue = new PartnerListSearchCriteria({
        [ListQueryParam.Active]: true,
        [ListQueryParam.Name]: filterValue.name,
      });
    } else {
      this.searchCriteriaValue = {
        ...this.searchCriteriaValue,
        ...filterValue,
        page: 0,
      };
    }
    this.partnerListFacade.loadListForDialog(new PartnerListSearchCriteria(this.searchCriteriaValue));
  }

  noPartnersExist(): boolean {
    const isSearchCriteriaInitialValue =
      !this.searchCriteriaValue[ListQueryParam.Name] && !this.searchCriteriaValue[ListQueryParam.PartnerTypeFilter];

    return isSearchCriteriaInitialValue && this.dataSource && this.dataSource.data.length === 0;
  }

  confirm(): void {
    this.dialogRef.close(this.partnerBriefList);
  }

  closeDialog(): void {
    this.dialogRef.close();
  }

  updatedPartnerList(): number[] {
    return this.partnerBriefList.map((partner: PartnersSelectionItem) => partner.id);
  }

  isAllSelected(): boolean {
    const numSelected = this.selection.selected.length;
    const numRows = this.dataSource.data.length;
    return numSelected === numRows;
  }

  masterToggle(): void {
    this.isAllSelected() ? this.removeData() : this.addData();
  }

  removeData(): void {
    this.dataSource.data.forEach((row: PartnerBrief) => {
      this.partnerBriefList.forEach((partner: PartnersSelectionItem, index: number) => {
        if (partner.id === row.id) {
          this.partnerBriefList.splice(index, 1);
        }
      });
    });
    this.selection.clear();
  }

  addData(): void {
    this.dataSource.data.forEach((row: PartnerBrief) => {
      this.partnerBriefList.forEach((partner: PartnersSelectionItem) => {
        if (partner.id === row.id) {
          this.partnerBriefList = this.partnerBriefList.filter((partnerElement: PartnersSelectionItem) => {
            return partnerElement.id !== row.id;
          });
        }
      });
    });
    this.dataSource.data.forEach((row: PartnerBrief) => {
      this.selection.select(row);
      this.partnerBriefList.push({ name: row.name, id: row.id, extIds: row.extIds });
    });
  }

  findType1Index(extIds: PartnerExtIdsModel[]): number {
    this.type1Index = extIds.findIndex((extId: PartnerExtIdsModel) => {
      return extId.type === PartnerServiceType.Tms || extId.type === PartnerServiceType.ShipTo;
    });
    return this.type1Index;
  }

  findType2Index(extIds: PartnerExtIdsModel[]): number {
    this.type2Index = extIds.findIndex((extId: PartnerExtIdsModel) => {
      return extId.type === PartnerServiceType.Sap || extId.type === PartnerServiceType.SoldTo;
    });
    return this.type2Index;
  }

  protected updateTable(tableDto: ListServerPaginationStateModel<PartnerBrief>): void {
    super.updateTable(tableDto);
    this.selection.clear();
    this.dataSource.data.forEach((row: PartnerBrief) => {
      if (
        this.partnerBriefList &&
        this.partnerBriefList.some((partner: PartnersSelectionItem) => row.id === partner.id)
      ) {
        this.selection.select(row);
      }
    });
  }

  public addOrRemoveRow(row: PartnerBrief, shouldAdd: boolean): void {
    if (shouldAdd) {
      this.partnerBriefList.push({ name: row.name, id: row.id, extIds: row.extIds });
    } else {
      const indexToRemove = this.partnerBriefList.findIndex(
        (item: PartnersSelectionItem) => item.name === row.name && item.id === row.id,
      );
      if (indexToRemove !== -1) {
        this.partnerBriefList.splice(indexToRemove, 1);
      }
    }
  }

  private initFiltersForm(): void {
    this.filterConfig = {
      items: [
        new FilterSearch({ name: ListQueryParam.Name }),
        new FilterSelect({
          name: ListQueryParam.PartnerTypeFilter,
          isMultiple: false,
          options: Object.values(PartnerType).map(
            (option: string) =>
              new SelectOption({
                name: 'common.filter.partnerType.option.' + option.toLowerCase(),
                value: option,
              }),
          ),
        }),
      ],
    };
  }
}
